import { SlidersHorizontal, ChevronUp, Check, RotateCcw } from "lucide-react";
import { SavedFilters } from "@/components/SavedFilters";
import DateRangePicker from "@/components/DateRangePicker";
import { logFiltersApplied } from "@/lib/audit";

// Shared premium, collapsible filter card used by both the Leads list and the
// Pipeline/Kanban view. Field set is driven by the `fields` prop so each page
// shows only the filters that make sense for it (logic & API unchanged).
const ctl = "h-10 w-full rounded-xl border border-sky bg-white px-3 text-sm text-navy outline-none transition-colors hover:border-loop/50 focus:border-loop focus:ring-2 focus:ring-loop/15 [color-scheme:light]";

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-ink/40">{label}</label>
      {children}
    </div>
  );
}

export default function FilterPanel({
  module, fields, options, users, user,
  draft, applied, appliedCount, draftCount, dirty,
  onChange, onApply, onReset, onCancel, onApplySaved,
}) {
  const has = (k) => fields.includes(k);
  const showOwner = user?.role !== "Sales Executive";
  const categoryOptions = draft.business_segment
    ? (options?.business_categories?.[draft.business_segment] || [])
    : Object.values(options?.business_categories || {}).flat();

  const SimpleSelect = ({ k, placeholder, opts }) => (
    <Field label={placeholder}>
      <select data-testid={`${module}-filter-${k}`} value={draft[k] || ""} onChange={(e) => onChange(k, e.target.value)} className={ctl}>
        <option value="">{placeholder}</option>
        {(opts || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );

  return (
    <div data-testid={`${module}-filter-panel`} className="overflow-hidden rounded-2xl border border-sky/70 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-sky/50 bg-mist/40 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-loop" />
          <h3 className="font-sora text-sm font-semibold text-navy">Filters</h3>
          {appliedCount > 0 && <span className="rounded-full bg-loop/10 px-2 py-0.5 text-[11px] font-bold text-loop">{appliedCount} active</span>}
        </div>
        <button onClick={onCancel} data-testid={`${module}-filter-hide`} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-ink/50 transition-colors hover:bg-white hover:text-navy">
          Hide filters <ChevronUp className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="space-y-4 p-4">
        <div className="grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {has("stage") && (
            <Field label="All stages">
              <select data-testid={`${module}-filter-stage`} value={draft.stage || ""} onChange={(e) => onChange("stage", e.target.value)} className={ctl}>
                <option value="">All stages</option>
                {(options?.stages || []).map((s) => <option key={s} value={s}>{options?.stage_meta?.[s]?.name || s}</option>)}
              </select>
            </Field>
          )}
          {has("quality") && <SimpleSelect k="quality" placeholder="All quality" opts={options?.qualities} />}
          {has("service_interested") && <SimpleSelect k="service_interested" placeholder="All services" opts={options?.services} />}
          {has("budget_range") && <SimpleSelect k="budget_range" placeholder="All budgets" opts={options?.budget_ranges} />}
          {has("business_segment") && <SimpleSelect k="business_segment" placeholder="All segments" opts={options?.business_segments} />}
          {has("business_category") && (
            <Field label="All categories">
              <select data-testid={`${module}-filter-business_category`} value={draft.business_category || ""} onChange={(e) => onChange("business_category", e.target.value)} className={ctl}>
                <option value="">All categories</option>
                {categoryOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          )}
          {has("owner_user_id") && showOwner && (
            <Field label="Owner">
              <select data-testid={`${module}-filter-owner`} value={draft.owner_user_id || ""} onChange={(e) => onChange("owner_user_id", e.target.value)} className={ctl}>
                <option value="">All owners</option>
                {users?.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
          )}
          {has("assigned") && (
            <Field label="Assignment">
              <select data-testid={`${module}-filter-assigned`} value={draft.assigned || ""} onChange={(e) => onChange("assigned", e.target.value)} className={ctl}>
                <option value="">Assigned & Unassigned</option>
                <option value="true">Assigned</option>
                <option value="false">Unassigned</option>
              </select>
            </Field>
          )}
          {has("follow_up_status") && (
            <Field label="Follow-up">
              <select data-testid={`${module}-filter-follow_up_status`} value={draft.follow_up_status || ""} onChange={(e) => onChange("follow_up_status", e.target.value)} className={ctl}>
                <option value="">Any follow-up</option>
                <option value="overdue">Overdue</option>
                <option value="today">Due today</option>
                <option value="upcoming">Upcoming</option>
                <option value="none">No follow-up</option>
              </select>
            </Field>
          )}
          {has("platform") && (
            <Field label="Platform">
              <input data-testid={`${module}-filter-platform`} value={draft.platform || ""} onChange={(e) => onChange("platform", e.target.value)} placeholder="e.g. Facebook" className={ctl} />
            </Field>
          )}
          {has("campaign_name") && (
            <Field label="Campaign contains">
              <input data-testid={`${module}-filter-campaign_name`} value={draft.campaign_name || ""} onChange={(e) => onChange("campaign_name", e.target.value)} placeholder="Campaign name…" className={ctl} />
            </Field>
          )}
          {has("created") && (
            <div className="min-w-0 sm:col-span-2">
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-ink/40">Created date</label>
              <DateRangePicker
                from={(draft.created_from || "").slice(0, 10)}
                to={(draft.created_to || "").slice(0, 10)}
                onChange={({ created_from, created_to }) => { onChange("created_from", created_from); onChange("created_to", created_to); }}
                testid={`${module}-date-range`}
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-sky/50 pt-3">
          <SavedFilters module={module} filters={applied} onApply={onApplySaved} activeCount={appliedCount} />
          <div className="flex items-center gap-2">
            <button onClick={onReset} disabled={!draftCount} data-testid={`${module}-filter-reset`} className="flex items-center gap-1.5 rounded-lg border border-sky px-3 py-2 text-sm font-medium text-ink/60 transition-colors hover:bg-mist disabled:opacity-40"><RotateCcw className="h-3.5 w-3.5" />Reset</button>
            <button onClick={onCancel} data-testid={`${module}-filter-cancel`} className="rounded-lg border border-sky px-3 py-2 text-sm font-medium text-ink/60 transition-colors hover:bg-mist">Cancel</button>
            <button onClick={() => { logFiltersApplied(module); onApply?.(); }} disabled={!dirty} data-testid={`${module}-filter-apply`} className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-loophover disabled:opacity-50"><Check className="h-4 w-4" />Apply Filters</button>
          </div>
        </div>
      </div>
    </div>
  );
}
